import { useState } from 'react'
import { useAsync } from 'react-use'
import { Col, Form, notification, Radio, Row, Space, Switch, Tooltip, Typography } from 'antd'
import { CloseOutlined, CheckOutlined, SettingOutlined } from '@ant-design/icons'
import _ from 'lodash'
import classNames from 'classnames'

import Page from 'components/Page'
import Card from 'components/Card'
import Button from 'components/Button'
import Modal from 'components/Modal'
import Input from 'components/Input'
import Select from 'components/Select'
import Table from 'components/Table'
import TextArea from 'components/TextArea'
import AllActiveInactive from 'components/AllActiveInactive'

import { useTranslate } from 'hooks/translate'

import { getValueListApi, getValueListByTypeCodeApi } from 'services/value_list'
import { getRiskMatrixApi, updateRiskMatrixApi } from 'services/riskMatrix'
import { getImpactTypeFilterListApi } from 'services/filter/impactType'

import Styled from './styled'

export default function PageRiskLevelSetup () {
  const { t } = useTranslate()

  const [form] = Form.useForm()

  const [dataSource, setDataSource] = useState([])
  const [impactLevelId, setImpactLevelId] = useState(null)
  const [impactLevelText, setImpactLevelText] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [filters, setFilters] = useState({
    param: null,
    is_active: null,
  })
  const [status, setStatus] = useState('all') // all, active, inactive
  const [isLoading, setIsLoading] = useState(false)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [groupLevel, setGroupLevel] = useState([])
  const [colorLevel, setColorLevel] = useState([])

  const getImpactLevelList = async () => {
    const res = await getImpactTypeFilterListApi()
    const filterImpactLevel = _.find(
      res.data,
      (item) => item?.value_list_name === 'ภาพรวมของผลกระทบ'
    )
    setImpactLevelId(filterImpactLevel?.id)
    setImpactLevelText(filterImpactLevel?.name)
  }

  const getValueList = async () => {
    const valueList = await getValueListApi()
    const groupTypeId = await getValueListByTypeCodeApi('RLG')
    const colorTypeId = await getValueListByTypeCodeApi('RLC')
    const groups = _.filter(
      valueList.data,
      (item) => item.type_id === groupTypeId?.data?.value_list_type_id
    )
    const colors = _.filter(
      valueList.data,
      (item) => item.type_id === colorTypeId?.data?.value_list_type_id
    )
    let formatGroup = groups.map((item) => {
      return { label: item.description, value: item.id }
    })
    let formatColor = colors.map((item) => {
      return { label: item.description, value: item.id, color: item.name }
    })
    setGroupLevel(formatGroup)
    setColorLevel(formatColor)
  }

  const getRiskMatrix = async () => {
    if (!impactLevelId || isLoading) return

    const params = {
      ...filters,
    }

    if (status !== 'all') {
      params.is_active = status === 'active'
    }

    setIsLoading(true)

    const res = await getRiskMatrixApi(impactLevelId, params)

    setIsLoading(false)

    setDataSource(res.data)
  }

  const updateRiskMatrix = async (id, values) => {
    const res = await updateRiskMatrixApi({
      id,
      risk_level_group_id: values?.group,
      risk_level_color_id: values?.color,
      risk_level_description: values?.description,
      is_active: values?.is_active,
      is_significant: values?.significant,
    })

    if (res.status === 'error') {
      notification.error({
        message: 'Failed',
        description: res.message,
      })
      return
    } else {
      notification.success({
        message: 'Updated Success',
        description: res.message,
      })
      getRiskMatrix()
    }

    setShowCreateModal(false)

    form.resetFields()
  }

  useAsync(async () => {
    getImpactLevelList()
    getRiskMatrix()
    getValueList()
  }, [impactLevelId, filters, status])

  const columns = [
    {
      title: t('Impact Level'),
      dataIndex: 'impact',
      key: 'impact',
      className: 'bold',
      sorter: (a, b) => a.impact.localeCompare(b.impact),
    },
    {
      title: t('Likelihood Level'),
      dataIndex: 'likelihood',
      key: 'likelihood',
      className: 'bold',
      sorter: (a, b) => a.likelihood.localeCompare(b.likelihood),
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      sorter: (a, b) => (a?.description || '').localeCompare(b?.description || ''),
      render: (text) => {
        return (
          <div>
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Group'),
      dataIndex: 'group_name',
      key: 'group_name',
      align: 'center',
      width: 80,
      sorter: (a, b) => (a?.group_name || '').localeCompare(b?.group_name || ''),
      render: (text) => {
        return (
          <div>
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Color'),
      dataIndex: 'color_name',
      key: 'color_name',
      align: 'center',
      width: 80,
      className: 'is-status',
      sorter: (a, b) => (a?.color_name || '').localeCompare(b?.color_name || ''),
      render: (text, record) => {
        return (
          <div className={classNames('status-block')} style={{ background: record.color_code }}>
            <Typography.Text>{text}</Typography.Text>
          </div>
        )
      },
    },
    {
      title: t('Significant'),
      dataIndex: 'significant',
      key: 'significant',
      width: 80,
      align: 'center',
      sorter: (a, b) => (`${a?.significant}` || '').localeCompare(`${b?.significant}` || ''),
      render: (checked) => {
        return (
          <Switch
            checkedChildren={<CheckOutlined />}
            unCheckedChildren={<CloseOutlined />}
            checked={checked}
            disabled
          />
        )
      },
    },
    {
      title: t('Actions'),
      dataIndex: 'actions',
      key: 'actions',
      width: 80,
      align: 'center',
      render: (text, record, index) => {
        return (
          <Space size='small'>
            <Tooltip title={t('Edit')}>
              <Button
                type='link'
                icon={<SettingOutlined />}
                onClick={() => {
                  setEditingId(record.id)

                  const editingDataSource = _.find(dataSource, { id: record.id })
                  form.setFieldsValue({
                    impact: editingDataSource.impact,
                    likelihood: editingDataSource.likelihood,
                    description: editingDataSource.description,
                    group: editingDataSource.group,
                    color: editingDataSource.color,
                    significant: editingDataSource.significant,
                    is_active: editingDataSource.is_active,
                  })

                  setShowCreateModal(true)
                }}
              />
            </Tooltip>
          </Space>
        )
      },
    },
  ]

  return (
    <Styled className='page page-report-attribute'>
      <Page
        title={t('Risk Level Setup')}
        searchPlaceholder={t('Search Impact Level or Likelihood Level')}
        onSearch={(value) => {
          setFilters({
            ...filters,
            param: value,
          })
        }}
      >
        <Space size='middle' direction='vertical' style={{ width: '100%' }}>
          <Row justify='space-between'>
            <Col>
              <Space size='middle'>
                <Typography.Text>{impactLevelText}</Typography.Text>
              </Space>
            </Col>
            <Col></Col>
          </Row>
          <Card title={t('Risk Level Setup')}>
            <Row justify='end'>
              <Col xs={24} sm={8} lg={4}>
                <AllActiveInactive value={status} onChange={(value) => setStatus(value)} />
              </Col>
            </Row>
            <Table
              loading={isLoading}
              columns={columns}
              dataSource={dataSource}
              scroll={{ x: 800 }}
              pagination={false}
              sticky
            />
          </Card>
        </Space>
      </Page>

      <Modal
        width={600}
        visible={showCreateModal}
        title={t('Update Risk Level Setup')}
        onCancel={() => {
          setShowCreateModal(false)
        }}
        afterClose={() => {
          if (editingId) {
            form.resetFields()
          }

          setEditingId(null)
        }}
        footer={[
          <Button
            key='create'
            type='primary'
            onClick={() => {
              form.submit()
            }}
          >
            {t('Update')}
          </Button>,
        ]}
      >
        <div>
          <Form
            form={form}
            layout='vertical'
            initialValues={{
              is_active: true,
            }}
            onFinish={async (values) => {
              if (editingId) {
                updateRiskMatrix(editingId, values)
              }
            }}
          >
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label={t('Impact Level')} name='impact'>
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('Likelihood Levil')} name='likelihood'>
                  <Input disabled />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item label={t('Description')} name='description'>
              <TextArea />
            </Form.Item>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item label={t('Group')} name='group'>
                  <Select placeholder={t('Select Group')} options={groupLevel} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('Color')} name='color'>
                  <Select placeholder={t('Select Color')} options={colorLevel} />
                </Form.Item>
              </Col>
            </Row>
            <Row justify='space-between'>
              <Col>
                <Form.Item name='significant'>
                  <Radio.Group value={form.getFieldValue('significant')}>
                    <Radio value={true}>{t('Significant')}</Radio>
                    <Radio value={false}>{t('Non-significant')}</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col>
                <Form.Item name='is_active'>
                  <Select
                    options={[
                      { label: t('Active'), value: true },
                      { label: t('Inactive'), value: false },
                    ]}
                    style={{ width: 200, textAlign: 'center' }}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Modal>
    </Styled>
  )
}
